/**
 * Shared Error Utilities
 *
 * Consolidated error handling patterns used across the codebase:
 * - Error code determination from error messages
 * - Safe error message extraction from various error types
 * - Error message sanitization to prevent information leakage
 * - User-friendly error message generation
 *
 * @module @loader/shared-types/utils/error-utils
 */

// ============================================================================
// Error Code Mappings
// ============================================================================

/**
 * Error code mappings for file processing operations.
 *
 * Each key is an error code, and the value is an array of keywords that,
 * when found in an error message (case-insensitive), indicate that error code.
 *
 * Order matters: earlier entries take precedence when multiple patterns match.
 */
export const FILE_PROCESSING_ERROR_CODES = {
  /** Storage-related errors (R2/S3) */
  R2_ERROR: ['r2', 's3', 'storage', 's3://'],
  /** File format or parsing errors */
  INVALID_FILE: ['invalid', 'parse', 'cannot read', 'unsupported file type'],
  /** Schema validation errors */
  SCHEMA_MISMATCH: ['schema', 'column', 'type mismatch', 'missing'],
  /** Default fallback for unclassified errors */
  PROCESSING_FAILED: [],
} as const;

/**
 * Error code mappings for schema extraction operations.
 *
 * Similar to FILE_PROCESSING_ERROR_CODES but includes extraction-specific codes.
 */
export const SCHEMA_EXTRACTION_ERROR_CODES = {
  /** Storage-related errors (R2/S3) */
  R2_ERROR: ['r2', 's3', 'storage', 's3://'],
  /** Unsupported file format */
  UNSUPPORTED_FORMAT: ['unsupported file type'],
  /** File format or parsing errors */
  INVALID_FILE: ['invalid', 'parse', 'cannot read'],
  /** Default fallback for unclassified errors */
  EXTRACTION_FAILED: [],
} as const;

/**
 * Type helper to extract error code types from a mapping
 */
export type ErrorCodeFromMapping<T extends Record<string, readonly string[]>> = keyof T;

export type FileProcessingErrorCode = ErrorCodeFromMapping<typeof FILE_PROCESSING_ERROR_CODES>;
export type SchemaExtractionErrorCode = ErrorCodeFromMapping<typeof SCHEMA_EXTRACTION_ERROR_CODES>;

// ============================================================================
// Error Code Determination
// ============================================================================

/**
 * Determines the appropriate error code based on error message content.
 *
 * Scans the error message for keywords defined in the error codes mapping
 * and returns the first matching error code. If no keywords match, returns
 * the default error code (the one with an empty keywords array).
 *
 * @param errorMessage - The error message to analyze
 * @param errorCodes - A mapping of error codes to their keyword patterns
 * @returns The determined error code
 *
 * @example
 * ```typescript
 * const code = determineErrorCode(
 *   'Failed to read from S3 bucket',
 *   FILE_PROCESSING_ERROR_CODES
 * );
 * // Returns: 'R2_ERROR'
 * ```
 */
export function determineErrorCode<T extends Record<string, readonly string[]>>(
  errorMessage: string,
  errorCodes: T
): keyof T {
  const lowerErrorMsg = errorMessage.toLowerCase();
  let defaultCode: keyof T | undefined;

  for (const [code, keywords] of Object.entries(errorCodes)) {
    // Track the default code (empty keywords array)
    if (keywords.length === 0) {
      defaultCode = code as keyof T;
      continue;
    }

    // Check if any keyword matches
    const hasMatch = keywords.some((keyword) => lowerErrorMsg.includes(keyword.toLowerCase()));
    if (hasMatch) {
      return code as keyof T;
    }
  }

  // Return default code or first code as fallback
  return defaultCode ?? (Object.keys(errorCodes)[0] as keyof T);
}

// ============================================================================
// Error Message Extraction
// ============================================================================

/**
 * Safely extracts an error message from any error type.
 *
 * Handles the following cases:
 * - Error instances: returns error.message
 * - Strings: returns the string directly
 * - Objects with message property: returns the message
 * - null/undefined: returns 'Unknown error'
 * - Other types: converts to string
 *
 * @param error - The error to extract a message from (unknown type)
 * @returns A string error message
 *
 * @example
 * ```typescript
 * extractErrorMessage(new Error('Something failed'));
 * // Returns: 'Something failed'
 *
 * extractErrorMessage({ message: 'API error' });
 * // Returns: 'API error'
 *
 * extractErrorMessage(null);
 * // Returns: 'Unknown error'
 * ```
 */
export function extractErrorMessage(error: unknown): string {
  // Handle null/undefined
  if (error === null || error === undefined) {
    return 'Unknown error';
  }

  // Handle Error instances
  if (error instanceof Error) {
    return error.message || 'Unknown error';
  }

  // Handle strings
  if (typeof error === 'string') {
    return error || 'Unknown error';
  }

  // Handle objects with message property
  if (typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') {
      return message || 'Unknown error';
    }
  }

  // Fallback: convert to string
  try {
    const str = String(error);
    return str === '[object Object]' ? 'Unknown error' : str;
  } catch {
    return 'Unknown error';
  }
}

/**
 * Extracts the stack trace from an error if available.
 *
 * @param error - The error to extract the stack from
 * @returns The stack trace string, or undefined if not available
 */
export function extractErrorStack(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.stack;
  }
  return undefined;
}

/**
 * Create error details object for logging
 *
 * Extracts both message and stack from an error for structured logging.
 *
 * @param error - Unknown error value
 * @returns Object with error message and optional stack
 */
export function getErrorDetails(error: unknown): {
  error: string;
  stack?: string;
} {
  return {
    error: extractErrorMessage(error),
    stack: extractErrorStack(error),
  };
}

// ============================================================================
// Error Message Sanitization
// ============================================================================

/**
 * Patterns to redact from error messages to prevent information leakage.
 */
const SANITIZATION_PATTERNS = [
  // S3/R2 paths
  { pattern: /s3:\/\/[^\s'"]+/gi, replacement: 's3://[redacted]' },
  // File paths with extensions
  { pattern: /\/[a-zA-Z0-9/_.-]+\.(parquet|csv|xlsx|xls|json|txt)/gi, replacement: '/[redacted].$1' },
  // Absolute paths (Unix-style)
  { pattern: /\/(?:tmp|var|home|usr|etc)\/[^\s'"]+/gi, replacement: '/[redacted]' },
  // Windows paths
  { pattern: /[A-Z]:\\[^\s'"]+/gi, replacement: '[redacted-path]' },
  // Connection strings
  { pattern: /postgresql:\/\/[^\s'"]+/gi, replacement: 'postgresql://[redacted]' },
  { pattern: /mysql:\/\/[^\s'"]+/gi, replacement: 'mysql://[redacted]' },
  // IP addresses (but not common localhost references)
  { pattern: /\b(?!127\.0\.0\.1\b)(?!0\.0\.0\.0\b)\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(?::\d+)?\b/g, replacement: '[ip-redacted]' },
];

/**
 * Maximum length for sanitized error messages.
 * Also used by the frontend to truncate error display.
 */
export const MAX_ERROR_MESSAGE_LENGTH = 500;

/**
 * Sanitizes error messages to prevent information leakage.
 *
 * Removes or redacts:
 * - S3/R2 bucket paths
 * - Local file system paths
 * - Connection strings
 * - IP addresses (except localhost)
 * - SQL identifiers (when appearing in "does not exist" errors)
 *
 * Also truncates messages longer than 500 characters.
 *
 * @param errorMessage - The raw error message to sanitize
 * @returns A sanitized error message safe for client responses
 */
export function sanitizeErrorForClient(errorMessage: string): string {
  let sanitized = errorMessage;

  // Apply all sanitization patterns
  for (const { pattern, replacement } of SANITIZATION_PATTERNS) {
    sanitized = sanitized.replace(pattern, replacement);
  }

  // Redact SQL identifiers in "does not exist" errors
  if (sanitized.includes('does not exist')) {
    sanitized = sanitized.replace(/"[^"]+"/g, '"[identifier]"');
  }

  // Truncate long messages
  if (sanitized.length > MAX_ERROR_MESSAGE_LENGTH) {
    sanitized = sanitized.substring(0, MAX_ERROR_MESSAGE_LENGTH) + '... [truncated]';
  }

  return sanitized;
}

// ============================================================================
// User-Friendly Error Messages
// ============================================================================

/**
 * Mapping of error codes to user-friendly messages.
 *
 * These messages are safe to display to end users and don't reveal
 * internal implementation details.
 */
const PUBLIC_ERROR_MESSAGES: Record<string, string> = {
  // File processing errors
  R2_ERROR: 'Storage operation failed. Please try again.',
  INVALID_FILE: 'The file could not be processed. Please check the file format.',
  SCHEMA_MISMATCH: 'The file structure does not match the expected schema.',
  PROCESSING_FAILED: 'File processing failed. Please try again or contact support.',

  // Schema extraction errors
  UNSUPPORTED_FORMAT: 'The file format is not supported. Please use CSV, Excel, or JSON files.',
  EXTRACTION_FAILED: 'Failed to extract file schema. Please check the file format.',

  // Query errors
  QUERY_TIMEOUT: 'The query took too long to execute. Please try a simpler query.',
  QUERY_EXECUTION_FAILED: 'Query execution failed. Please check your query syntax.',

  // Authentication/Authorization errors
  INVALID_TOKEN: 'Invalid or expired authentication token.',
  FORBIDDEN_QUERY: 'You do not have permission to execute this query.',

  // Generic fallback
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again or contact support.',
};

/**
 * Returns a user-friendly error message for a given error code.
 *
 * These messages are safe to display to end users and intentionally
 * avoid revealing internal implementation details or system information.
 *
 * @param errorCode - The error code to get a message for
 * @returns A user-friendly error message
 */
export function getPublicErrorMessage(errorCode: string): string {
  return PUBLIC_ERROR_MESSAGES[errorCode] ?? PUBLIC_ERROR_MESSAGES.UNKNOWN_ERROR;
}

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Processes an error for client response in a single call.
 *
 * Combines error message extraction, sanitization, and error code determination
 * into a single convenience function.
 *
 * @param error - The error to process
 * @param errorCodes - The error code mapping to use for classification
 * @returns An object with the determined error code and sanitized message
 */
export function processErrorForClient<T extends Record<string, readonly string[]>>(
  error: unknown,
  errorCodes: T
): {
  errorCode: keyof T;
  sanitizedMessage: string;
  publicMessage: string;
  rawMessage: string;
} {
  const rawMessage = extractErrorMessage(error);
  const errorCode = determineErrorCode(rawMessage, errorCodes);
  const sanitizedMessage = sanitizeErrorForClient(rawMessage);
  const publicMessage = getPublicErrorMessage(errorCode as string);

  return {
    errorCode,
    sanitizedMessage,
    publicMessage,
    rawMessage,
  };
}

/**
 * Type guard to check if a value is an Error instance.
 *
 * @param value - The value to check
 * @returns True if the value is an Error instance
 */
export function isError(value: unknown): value is Error {
  return value instanceof Error;
}

// ============================================================================
// Version Error Details (stored as JSONB in versions.error_details)
// ============================================================================

/**
 * Runtime type guard for VersionErrorDetails.
 *
 * Validates that a value has the minimum shape of a VersionErrorDetails object:
 * an object with a `category` string property. Use this instead of unsafe `as` casts
 * when receiving JSONB data from the database or untyped API responses.
 *
 * @param val - The value to check
 * @returns True if the value matches the VersionErrorDetails shape
 */
export function isVersionErrorDetails(val: unknown): val is VersionErrorDetails {
  return typeof val === 'object' && val !== null && 'category' in val && typeof (val as Record<string, unknown>).category === 'string';
}

/**
 * Error category for version processing failures.
 *
 * Determines how the frontend renders the error dialog:
 * - schema_mismatch: Shows expected/actual/missing column lists
 * - data_validation: Shows affected fields with null/uniqueness counts
 * - append_conflict: Shows fields with cross-version uniqueness conflicts
 * - Other categories: Show the raw error message
 */
export type VersionErrorCategory =
  | 'schema_mismatch'
  | 'data_validation'
  | 'append_conflict'
  | 'parse'
  | 'conversion'
  | 'timeout'
  | 'storage'
  | 'connection'
  | 'access'
  | 'unknown';

/** Field-level error detail for validation and append errors */
export interface VersionErrorFieldDetail {
  /** Field name */
  name: string;
  /** Human-readable reason (e.g., "2 null value(s) on rows 3, 7") */
  reason: string;
  /** Target data type for conversion errors (e.g., "integer") */
  toType?: string;
  /** Number of offending values */
  count?: number;
  /** Sample row numbers where violations occur (1-indexed, up to 10) */
  sampleRows?: number[];
  /** Sample conflicting values (up to 5) */
  sampleValues?: string[];
  /** Per-line error details: each entry pairs a row number with the offending value */
  lines?: Array<{ row: number; value?: string; versionNumber?: number }>;
}

/**
 * Structured error details for failed versions.
 *
 * Stored as JSONB in the `error_details` column of the `versions` table.
 * The frontend uses this to render a structured error dialog instead of
 * regex-parsing the `error_message` text field.
 *
 * The `error_message` text field is kept as a human-readable fallback.
 */
export interface VersionErrorDetails {
  /** Error category — determines frontend rendering */
  category: VersionErrorCategory;
  /** Standardized error code from FILE_PROCESSING_ERROR_CODES */
  errorCode: string;

  // Schema mismatch fields
  /** Columns missing from the uploaded file */
  missingColumns?: string[];
  /** Columns expected by the table schema */
  expectedColumns?: string[];
  /** Columns found in the uploaded file */
  actualColumns?: string[];

  // Validation / append conflict fields
  /** Per-field error details */
  fields?: VersionErrorFieldDetail[];

  /** True when the backend stopped collecting errors before exhausting all violations */
  truncated?: boolean;
}
